import React, { useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import _ from '@/utils/lodash';
import cx from 'classnames';
import Html from '@components/page/Html';
import Masonry from '@components/page/Masonry';
import DirectoryMasonry from '@components/page/DirectoryMasonry';
import Slideshow from '@components/page/Slideshow';
import BlockRenderer, { OverlayBlocks } from '@components/page/BlockRenderer';
import { pageContentCentered } from '@/pages/content-align';
import ImageViewer from '@components/shared/ImageViewer';
import FullscreenBackground from '@components/shared/FullscreenBackground';
import { useAppContext } from '@/context/AppContext';
import { getPagePath } from '@/utils/pagePaths';
import { useEditorFrame, useBlockEditing } from '@components/preview/editGates';
import { hasBackground } from '@/background/sets';

/**
 * Home Page - app/page.js
 * Route: /
 * Uses semantic HTML: section
 * 
 * Supports gallery types:
 * - 'none': Text only (shows content)
 * - 'masonry': Masonry grid with optional content above
 * - 'slideshow': Editorial slideshow with content overlay
 */
function HomePage() {
    const navigate = useNavigate();
    const { content, theme } = useAppContext();
    const inEditor = useEditorFrame();
    const textEditing = useBlockEditing('legacy-richtext');
    const home = content?.home || {};
    const pages = content?.pages || {};
    const files = content?.files || {};
    const properties = content?.properties || {};
    
    // Home page data with defaults (gallery properties are now nested)
    const galleryData = home.gallery || {};
    const images = galleryData.images || [];
    const columns = galleryData.columns ?? 5;
    const breakpoints = galleryData.breakpoints ?? null;
    const gap = galleryData.gap ?? 4;
    const crops = galleryData.crops || null;
    const links = galleryData.links || [];
    const galleryType = galleryData.type || 'masonry';
    // Blocks only exist on home once it holds more than one text and one gallery,
    // or in the editor preview. Slideshow and fullscreen ignore them. An empty
    // blocks array means the stack was cleared — do not fall back to the gallery.
    const useBlocks = Array.isArray(home.blocks)
        && galleryType !== 'slideshow' && galleryType !== 'background';
    const homeContent = home.content || '';
    const contentMaxWidth = home.contentMaxWidth ?? 65;
    const name = home.name || 'Home';
    
    // Theme colours. Only home's own overrides are written inline; otherwise the
    // concrete colours resolve through the site theme's CSS variables, so the
    // Appearance preview and a published theme both take effect here.
    const pageBackground = home.themeBackground || null;
    const pageText = home.themeText || null;
    const themeVars = {
        ...(pageBackground ? { '--color-background': pageBackground } : {}),
        ...(pageText ? { '--color-text': pageText } : {}),
        backgroundColor: 'var(--color-background)',
        color: 'var(--color-text)',
    };
    
    // Create a page-like object for Html/Slideshow components
    const homePageData = {
        content: homeContent,
        contentMaxWidth,
        name,
        images,
    };

    // For HTML content (text only), use 30/70 vertical distribution instead of centered
    const isHtmlOnly = galleryType === 'none';

    // Check for fullscreen background display type
    const isBackgroundMode = galleryType === 'background' && hasBackground(home.backgroundImage, files);
    
    // Get follow-up page URL for background mode
    const followUpUrl = useMemo(() => {
        if (!isBackgroundMode || !home.advanceUrl) return null;
        // "home" shouldn't be selectable from home page, but handle it gracefully
        if (home.advanceUrl === 'home') return null;
        const followUpPage = pages[home.advanceUrl];
        if (!followUpPage) return null;
        const path = getPagePath(followUpPage, pages);
        return path ? `/${path}` : null;
    }, [isBackgroundMode, home.advanceUrl, pages]);
    
    // Handle navigation to follow-up page
    const handleAdvance = useCallback(() => {
        if (followUpUrl) {
            navigate(followUpUrl);
        }
    }, [followUpUrl, navigate]);

    // In Appearance, only block advance while overlay text is open for editing.
    const canAdvance = Boolean(followUpUrl) && !textEditing;
    
    // Keyboard navigation for background mode
    useEffect(() => {
        if (!isBackgroundMode || !canAdvance) return;
        
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                handleAdvance();
            }
        };
        
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isBackgroundMode, canAdvance, handleAdvance]);

    // For background mode, render a full-viewport clickable area with centered content
    if (isBackgroundMode) {
        return (
            <section 
                className={cx(
                    'fs-stage flex flex-col items-center justify-center',
                    { 'cursor-pointer': canAdvance }
                )}
                aria-label="Home"
                data-page-path="page.advance"
                onClick={canAdvance ? (event) => {
                    // The text sits on the click-through. In the editor, a click
                    // there selects or opens the text instead of leaving the page.
                    if (inEditor && event.target?.closest?.('[data-page-path="page.block"], [data-page-path="page.content"]')) return;
                    handleAdvance();
                } : undefined}
                style={themeVars}
            >
                <FullscreenBackground
                    backgroundImage={home.backgroundImage}
                    files={files}
                />
                {/* Overlay may exceed the viewport; section clips without scrollbars */}
                <div
                    className={cx(
                        'html-container relative z-10 text-center w-full overflow-visible',
                        inEditor && 'cursor-auto',
                    )}
                    style={{ zIndex: 10 }}
                    data-page-path="page.block"
                    data-page-id="legacy-richtext"
                    data-editor-overlay={inEditor ? '1' : undefined}
                    data-block-editing={textEditing ? '1' : undefined}
                    onClick={(inEditor || textEditing) ? (event) => event.stopPropagation() : undefined}
                >
                    <OverlayBlocks
                        page={home}
                        pages={pages}
                        files={files}
                        theme={theme}
                        gallery="background"
                        editing={inEditor ? textEditing : undefined}
                        editPath="content"
                    />
                </div>
            </section>
        );
    }

    return (
        <section 
            className={cx(
                'w-full shrink-0 min-h-full flex flex-col items-center relative',
                { 
                    'justify-start pb-[24vh]': isHtmlOnly && !useBlocks && !pageContentCentered(home, isHtmlOnly),
                    'justify-center': pageContentCentered(home, isHtmlOnly),
                }
            )}
            aria-label="Home"
            data-page-path="page.display"
            style={themeVars}
        >
            {useBlocks && (
                <BlockRenderer
                    blocks={home.blocks}
                    page={home}
                    pages={pages}
                    files={files}
                    fullPagePath={null}
                />
            )}

            {/* Show content for text-only and masonry modes (not slideshow - it has its own overlay) */}
            {!useBlocks && galleryType !== 'slideshow' && homeContent && (
                <Html 
                    content={homeContent} 
                    contentMaxWidth={contentMaxWidth} 
                    name={name}
                    pages={pages} 
                    files={files} 
                    gallery={galleryType} 
                />
            )}
            
            {/* Masonry gallery */}
            {!useBlocks && galleryType === 'masonry' && (
                <>
                    <Masonry 
                        images={images.length > 2 ? images : []} 
                        url={null} 
                        columns={columns} 
                        breakpoints={breakpoints}
                        gap={gap}
                        crops={crops}
                    />
                    {/* Single image fallback for 1-2 images */}
                    {images.length === 1 && (
                        <ImageViewer match={{ params: { image: images[0] } }} />
                    )}
                </>
            )}
            
            {/* Directory gallery */}
            {!useBlocks && galleryType === 'directory' && (
                <DirectoryMasonry
                    links={links}
                    columns={columns}
                    breakpoints={breakpoints}
                    gap={gap}
                />
            )}
            
            {/* Slideshow gallery */}
            {galleryType === 'slideshow' && (
                <Slideshow {...homePageData} images={images} url={null} page={home} />
            )}
        </section>
    );
}

export default HomePage;
